import React, { useId } from 'react';

import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { TNumerologyType } from './constants';

import Url from '../../../../services/Url';

import useNumerologyCalculatorViewModel from './hooks/useNumerologyCalculatorViewModel';


import './NumerologyCalculator.style.scss';

interface INumerologyCalculatorProps {
    /** Variação de textos e da pergunta enviada à Orbia. */
    type: TNumerologyType;
}

/**
 * Calculadora de numerologia do corpo do artigo: formulário de nome e, depois do cálculo, o número de
 * expressão com o CTA de interpretação na Orbia. Montada pelo módulo `numerologycalculator`.
 */
const NumerologyCalculator: React.FC<INumerologyCalculatorProps> = ({ type }) => {
    const intl = useIntl();
    const inputId = useId();
    const errorId = `${inputId}-error`;

    const {
        name,
        error,
        isLoading,
        result,
        handleNameChange,
        handleSubmit,
        handleInterpretation,
        handleNewCalculation
    } = useNumerologyCalculatorViewModel(type);

    const isCalculated = result !== null;

    return (
        <section className="numerology-calculator" aria-live="polite">
            <div className={classNames('numerology-calculator__title', { 'numerology-calculator__title--result': isCalculated })}>
                <FormattedMessage id={`numerology.types.${type}.${isCalculated ? 'result.title' : 'title'}`} />
            </div>

            <div className={classNames('numerology-calculator__subtitle', { 'numerology-calculator__subtitle--result': isCalculated })}>
                {isCalculated ? name.toUpperCase() : <FormattedMessage id={`numerology.types.${type}.subtitle`} />}
            </div>

            {isCalculated ? (
                <>
                    <img
                        className="numerology-calculator__avatar"
                        src={Url.image('neo-structure/chatai/static-orbia.gif')}
                        width={80}
                        height={78}
                        alt=""
                    />

                    <div className="numerology-calculator__number">
                        <FormattedMessage id={`numerology.types.${type}.result.label`} />
                        {result}
                    </div>

                    <div className="numerology-calculator__description">
                        <FormattedMessage id={`numerology.types.${type}.result.description`} />
                    </div>

                    <button
                        type="button"
                        className="numerology-calculator__button numerology-calculator__button--interpretation"
                        onClick={handleInterpretation}
                    >
                        <FormattedMessage id="numerology.general.resultButton" />
                    </button>

                    <button type="button" className="numerology-calculator__reset" onClick={handleNewCalculation}>
                        <FormattedMessage id="numerology.general.newCalculation" />
                    </button>
                </>
            ) : (
                <form className="numerology-calculator__form" onSubmit={handleSubmit} noValidate>
                    <label className="numerology-calculator__label" htmlFor={inputId}>
                        <FormattedMessage id={`numerology.types.${type}.label`} />
                    </label>

                    <input
                        id={inputId}
                        type="text"
                        autoComplete="name"
                        className={classNames('numerology-calculator__input', { 'numerology-calculator__input--error': !!error })}
                        placeholder={intl.formatMessage({ id: `numerology.types.${type}.placeholder` })}
                        value={name}
                        onChange={handleNameChange}
                        aria-invalid={!!error}
                        aria-describedby={error ? errorId : undefined}
                    />

                    {Boolean(error) && (
                        <div id={errorId} className="numerology-calculator__error" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="numerology-calculator__info">
                        <span className="numerology-calculator__info-title">
                            <FormattedMessage id="numerology.general.importantTitle" />
                        </span>
                        <FormattedMessage id="numerology.general.importantInfo" />
                    </div>

                    <button
                        type="submit"
                        className={classNames('numerology-calculator__button', { 'numerology-calculator__button--loading': isLoading })}
                        disabled={isLoading}
                        aria-busy={isLoading}
                    >
                        {isLoading ? (
                            <img
                                className="numerology-calculator__loader"
                                src={Url.image('loader/inverted.svg')}
                                alt={intl.formatMessage({ id: 'numerology.general.loadingButton' })}
                            />
                        ) : (
                            <FormattedMessage id={`numerology.types.${type}.button`} />
                        )}
                    </button>
                </form>
            )}
        </section>
    );
};

export default NumerologyCalculator;
